/* HUENIT LAB 2.0 — Admin: 대시보드.
   주요 지표(실데이터): 예제 수(S3 manifest)·사용자 수·대기 요청 수(관리 API).
   콘텐츠 현황: 카테고리/필터/가이드/언어(CMS 전용 localStorage).
   live/fake 모두 안전 — 각 지표는 독립 로드, 실패 시 '—' placeholder(대시보드는 막지 않음).
   카드 클릭 → 해당 섹션(go = App 이 주입한 셸 네비게이션). */
import { useEffect, useState } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { Spinner } from '../../components/index.js';
import { useSession } from '../../auth/session.jsx';
import {
  useLocal, LANG_SEED, SEED_EX_CATS, SEED_EX_FILTERS, SEED_ENGRAVE_CATS, SEED_ENGRAVE_FILTERS, SEED_GUIDE,
} from '../../data/store.js';
import { listExamples } from '../../features/examples/repo.js';
import { listUsers, listRequests } from '../../api/adminApi.js';

/* 단일 비동기 지표 로더 — { loading, value, error }. */
function useMetric(fn, deps) {
  const [state, setState] = useState({ loading: true, value: null, error: false });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, value: null, error: false });
    (async () => {
      try {
        const value = await fn();
        if (!cancelled) setState({ loading: false, value, error: false });
      } catch (e) {
        // 대시보드는 실패해도 막지 않는다 — placeholder 로 강등(콘솔만 조용히).
        if (!cancelled) setState({ loading: false, value: null, error: true });
      }
    })();
    return () => { cancelled = true; };
  }, deps);   // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

/* 실데이터 지표 카드 — 로딩=스피너, 실패='—'(hint), 성공=값. */
function MetricCard({ icon, label, metric, suffix = '', hint, to, go }) {
  const { loading, value, error } = metric;
  return (
    <button className="stat" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => go(to)}>
      <div className="stat__k"><Icon name={icon} size={15} />{label}</div>
      <div className="stat__v">
        {loading ? <Spinner size={18} />
          : error ? <span style={{ color: 'var(--ink-300)' }} title={hint || '데이터를 불러오지 못했습니다.'}>—</span>
          : <>{value}{suffix}</>}
      </div>
    </button>
  );
}

export function Dashboard({ go }) {
  const { mode, getIdToken } = useSession();

  // CMS 전용(localStorage) 콘텐츠 현황 — 즉시 사용 가능.
  // 분류는 코드 예제 축 + 그림·툴패스 축 2개다 — 현황 타일은 합계를 보여준다.
  const [cats] = useLocal('adm.exCats', SEED_EX_CATS);
  const [filters] = useLocal('adm.exFilters', SEED_EX_FILTERS);
  const [engCats] = useLocal('adm.engraveCats', SEED_ENGRAVE_CATS);
  const [engFilters] = useLocal('adm.engraveFilters', SEED_ENGRAVE_FILTERS);
  const [guide] = useLocal('adm.guide', SEED_GUIDE);
  const [langs] = useLocal('adm.langs', LANG_SEED);

  const smallTotal = guide.reduce((n, c) => n + c.big.reduce((m, b) => m + b.small.length, 0), 0);
  const bigTotal = guide.reduce((n, c) => n + c.big.length, 0);

  // 실데이터 지표 — 각자 독립 로드(하나 실패해도 나머지는 표시).
  const examples = useMetric(() => listExamples().then(items => items.length), [mode]);
  const users = useMetric(
    () => listUsers(getIdToken, { limit: 60 }).then(r => ({ n: r.users.length, more: !!r.paginationToken })),
    [mode, getIdToken],
  );
  const requests = useMetric(
    () => listRequests(getIdToken, 'pending').then(r => r.requests.length),
    [mode, getIdToken],
  );

  const CORS_HINT = '관리 API에 연결하지 못했습니다. (API Gateway CORS·로그인 상태 확인)';

  return (
    <div>
      {/* 주요 지표 — 실데이터 */}
      <div className="stat-row">
        <MetricCard icon="folder" label="공식 예제" to="examples" go={go}
          metric={{ ...examples }} hint="예제 목록(S3)을 불러오지 못했습니다." />
        <MetricCard icon="user" label="사용자" to="users" go={go}
          metric={{ ...users, value: users.value && users.value.n }}
          suffix={users.value && users.value.more ? '+' : ''} hint={CORS_HINT} />
        <MetricCard icon="bell" label="대기 중인 요청" to="teacher-requests" go={go}
          metric={{ ...requests }} hint={CORS_HINT} />
      </div>

      {/* 콘텐츠 현황 — CMS 전용(localStorage) */}
      <div className="panel" style={{ marginBottom: 24 }}>
        <div className="panel__head">
          <div>
            <div className="panel__title">콘텐츠 현황</div>
            <div className="panel__sub">분류·가이드·언어는 이 콘솔에만 저장됩니다(브라우저).</div>
          </div>
        </div>
        <div className="panel__body">
          <div className="stat-row" style={{ marginBottom: 0 }}>
            {[
              { k: '예제 카테고리', v: cats.length + engCats.length, icon: 'blocks', to: 'taxonomy' },
              { k: '필터 태그', v: filters.length + engFilters.length, icon: 'puzzle', to: 'taxonomy' },
              { k: '가이드 카테고리', v: guide.length, icon: 'guide', to: 'guide' },
              { k: '큰 주제 / 작은 주제', v: bigTotal + ' / ' + smallTotal, icon: 'book', to: 'guide' },
              { k: '지원 언어', v: langs.length, icon: 'globe', to: 'languages' },
            ].map((s, i) => (
              <button key={i} className="stat" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => go(s.to)}>
                <div className="stat__k"><Icon name={s.icon} size={15} />{s.k}</div>
                <div className="stat__v">{s.v}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 빠른 작업 */}
      <div className="panel">
        <div className="panel__head"><div className="panel__title">빠른 작업</div></div>
        <div className="panel__body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn--soft" onClick={() => go('examples')}><Icon name="plus" size={15} />예제 등록</button>
          <button className="btn btn--soft" onClick={() => go('users')}><Icon name="user" size={15} />사용자 관리</button>
          <button className="btn btn--soft" onClick={() => go('teacher-requests')}><Icon name="bell" size={15} />요청 처리</button>
          <button className="btn btn--soft" onClick={() => go('guide')}><Icon name="guide" size={15} />가이드 편집</button>
          <button className="btn btn--soft" onClick={() => go('taxonomy')}><Icon name="blocks" size={15} />분류 관리</button>
          <button className="btn btn--soft" onClick={() => go('languages')}><Icon name="globe" size={15} />언어 설정</button>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
